import { inject, Injectable, NgZone } from "@angular/core";
import posthog from "posthog-js";
import { environment } from "../../../environments/environment";
import { getCurrentUser } from "aws-amplify/auth";

@Injectable({
  providedIn: 'root',
})

export class PosthogService {
  private ngZone = inject(NgZone);

  constructor() {
    this.initPostHog();
  }

  private initPostHog() {
    this.ngZone.runOutsideAngular(() => {
      posthog.init(
        environment.posthog.apiKey, {
        api_host: environment.posthog.host,
        person_profiles: 'identified_only',
        disable_session_recording: true,  // ✅ Désactiver l'enregistrement par défaut
          session_recording: {
            recordCrossOriginIframes: true,
            maskAllInputs: false,
            maskTextSelector: '[data-posthog-mask]',
          },
          autocapture: {
            dom_event_allowlist: ['click', 'change', 'submit'],
            element_allowlist: ['a', 'button', 'form', 'input', 'select', 'textarea'],
          },
      });

      // Register environment as a super property (included in all events)
      posthog.register({
        environment: environment.name,
        is_production: environment.production
      });

      console.log('📊 PostHog initialized - Session recording disabled until sign-in');
    });
  }

  async identifyUser(fullUser: any) {
    const userEmail = fullUser.signInDetails?.loginId;

    posthog.identify(fullUser.userId);

    // Set person properties for immediate feature flag evaluation
    posthog.setPersonPropertiesForFlags({
      '$email': userEmail,
      'email': userEmail,
      'aws_user_id': fullUser.userId
    });

    posthog.register({
      $email: userEmail,
      aws_user_id: fullUser.userId
    });
    posthog.people.set({
      $email: userEmail,
      aws_user_id: fullUser.userId
    });

    // ✅ Démarrer l'enregistrement de session seulement après connexion
    posthog.startSessionRecording();
    console.log('✅ PostHog session recording started for user:', fullUser.userId);
    console.log('✅ PostHog person properties set for flags:', userEmail);
  }

  // ✅ Arrêter l'enregistrement lors de la déconnexion
  resetUser() {
    posthog.stopSessionRecording();
    posthog.reset();
    console.log('🛑 PostHog session recording stopped - User signed out');
  }

  async capture(eventName: string, properties = {}) {
    const fullUser = await getCurrentUser();
    posthog.capture(eventName, {
      ...properties,
      $email: fullUser.signInDetails?.loginId,
      aws_user_id: fullUser.userId
    });
  }

  featureFlagAI(): boolean{
    return !!posthog.isFeatureEnabled('AI-suggest-Invoice-desciption')
  }

  featureFlagCRM(): boolean {
    return !!posthog.isFeatureEnabled('crm-access')
  }

  featureFlagTasks(): boolean {
    return !!posthog.isFeatureEnabled('tasks-access')
  }

  // Generic feature flag checker
  isFeatureEnabled(flagKey: string): boolean {
    return !!posthog.isFeatureEnabled(flagKey)
  }

  // Get feature flag value (for multivariate flags)
  getFeatureFlag(flagKey: string): string | boolean | undefined {
    return posthog.getFeatureFlag(flagKey)
  }

  // Get feature flag payload
  getFeatureFlagPayload(flagKey: string): any {
    return posthog.getFeatureFlagPayload(flagKey)
  }

  // Wait for feature flags to load
  onFeatureFlags(callback: () => void): void {
    posthog.onFeatureFlags(callback)
  }

  // Reload feature flags
  reloadFeatureFlags(): void {
    posthog.reloadFeatureFlags()
  }
}
